import { Helmet } from "react-helmet-async";
import { BRAND, getSection } from "@/lib/siteSeo";

// -----------------------------------------------------------------------------
// SeoHead — drop this at the top of any page render to inject the correct
// <title>, meta description, canonical, and Open Graph / Twitter card values.
//
// Hostname-aware: on `foodpulse.net` (or any `/food-pulse/*` route) the
// component switches to the FoodPulse brand defaults so we never leak
// "SocialPulse | Global Community…" as the tab title on a food page.
// Every page also receives a canonical URL pointing at the current
// hostname + path so duplicate content across preview / prod / mobile
// shells consolidates.
// -----------------------------------------------------------------------------

const DEFAULT_SP_IMAGE = "https://socialpulse.com/assets/social-pulse-logo.jpg";
const DEFAULT_FP_IMAGE = "/api/og/host-default.png"; // hostname-aware backend route

function isFoodPulseSurface() {
  try {
    const host = String(window.location.hostname || "").toLowerCase();
    const path = String(window.location.pathname || "").toLowerCase();
    return /foodpulse/i.test(host)
      || /^\/(food-pulse|basket|pantry|receipts|smart-basket|install)(\/|$)/.test(path);
  } catch { return false; }
}

function buildCanonical(url) {
  if (url) return url;
  try {
    // Strip query strings / hashes so the same canonical is emitted
    // regardless of ?utm_ / ?ref= / ?tab= variants — audit fix.
    const { protocol, hostname, pathname } = window.location;
    return `${protocol}//${hostname}${pathname}`;
  } catch { return undefined; }
}

export default function SeoHead({
  section,
  title,
  description,
  image,
  url,
  type = "website",
}) {
  const fpSurface = isFoodPulseSurface();
  const cfg = section ? getSection(section) : (fpSurface ? getSection("food-pulse") : {});
  const brandName = fpSurface ? "FoodPulse" : BRAND.name;
  const fallbackTitle = fpSurface
    ? "FoodPulse — Fresh food. Better prices. Less waste."
    : "SocialPulse | Global Community, Family Tree & Memorial Platform";
  const fallbackDesc = fpSurface
    ? "Discover daily deals from local farms, supermarkets and food businesses, scan labels, cut food waste, and share surplus with your community."
    : "Discover communities, build your family tree, create memorials, explore local places, events, businesses, food, history, Civic Reports and more with SocialPulse.";
  const fallbackImage = fpSurface ? DEFAULT_FP_IMAGE : DEFAULT_SP_IMAGE;

  const resolvedTitle =
    title ||
    cfg.meta_title ||
    (cfg.tagline
      ? `${cfg.tagline.replace(/\.$/, "")} | ${brandName}`
      : fallbackTitle);
  const resolvedDesc = description || cfg.meta_desc || fallbackDesc;
  const resolvedImage = image || fallbackImage;
  const ogDesc = cfg.og_desc || resolvedDesc;
  const canonical = buildCanonical(url);

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDesc} />
      {cfg.tagline && <meta name="tagline" content={cfg.tagline} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:site_name" content={brandName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={ogDesc} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={resolvedImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={ogDesc} />
      <meta name="twitter:image" content={resolvedImage} />
    </Helmet>
  );
}
